import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  OnChanges,
  SimpleChanges,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import * as am5 from '@amcharts/amcharts5';
import { Graphe } from '../../Services/EntiteProjet';
import { Installation as InstallationService, ModeAffichageGraphique } from '../../Services/installation';
import { ChartHostOverlayComponent } from '../chart-host-overlay/chart-host-overlay';
import { ChartFullscreenModalComponent } from '../chart-fullscreen-modal/chart-fullscreen-modal';
import { ChartPeriodSelectorComponent, PeriodeChangee } from '../chart-period-selector/chart-period-selector';
import { GrapheDataPoint, createGrapheChart } from '../../Charts/graphe-chart';

@Component({
  selector: 'app-graphe-card',
  imports: [ChartHostOverlayComponent, ChartFullscreenModalComponent, ChartPeriodSelectorComponent],
  templateUrl: './graphe-card.html',
  styleUrl: './graphe-card.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GrapheCardComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) graphe!: Graphe;
  @Input({ required: true }) installationId!: number;

  private installationService = inject(InstallationService);
  private cdr = inject(ChangeDetectorRef);
  private platformId = inject(PLATFORM_ID);

  chargement = true;
  erreur = '';
  modaleVisible = false;
  chargementModal = false;
  modeModal: ModeAffichageGraphique = 'jour';
  dateFinModal: Date = new Date();
  refreshToken = 0;

  private donneesActuelles: GrapheDataPoint[] = [];
  private chartRoot?: am5.Root;
  private chartHost?: HTMLDivElement;
  private donneesEnAttente?: GrapheDataPoint[];
  private requeteInitiale = 0;
  private requeteModal = 0;

  readonly chartRendererModal = (host: HTMLDivElement): am5.Root =>
    createGrapheChart(host, this.graphe, this.donneesActuelles);

  ngOnInit(): void {
    this.modeModal = this.intervalVersMode(this.graphe.interval);
    this.dateFinModal = new Date();
    void this.chargerDonnees(this.modeModal, this.dateFinModal, true);
  }

  ngOnDestroy(): void {
    ++this.requeteInitiale;
    ++this.requeteModal;
    this.chartRoot?.dispose();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.values(changes).some(change => !change.firstChange)) {
      ++this.requeteInitiale;
      ++this.requeteModal;
      this.chartRoot?.dispose();
      this.donneesActuelles = [];
      this.donneesEnAttente = undefined;
      this.erreur = '';
      this.chargement = true;
      this.chargementModal = false;
      this.modaleVisible = false;
      this.ngOnInit();
    }
  }

  onChartHostReady(host: HTMLDivElement): void {
    this.chartHost = host;
    if (this.donneesEnAttente !== undefined) {
      this.initialiserGraphiqueCarteInternal(this.donneesEnAttente);
      this.donneesEnAttente = undefined;
    }
  }

  ouvrirModale(): void {
    this.modaleVisible = true;
    this.cdr.markForCheck();
  }

  fermerModale(val: boolean): void {
    this.modaleVisible = val;
    this.cdr.markForCheck();
  }

  onPeriodeChangeModal({ mode, dateFin }: PeriodeChangee): void {
    this.modeModal = mode;
    this.dateFinModal = dateFin;
    this.chargementModal = true;
    this.cdr.markForCheck();
    void this.chargerDonnees(mode, dateFin, false);
  }

  private async chargerDonnees(
    mode: ModeAffichageGraphique,
    dateFin: Date,
    initialiser: boolean,
  ): Promise<void> {
    const requete = initialiser ? ++this.requeteInitiale : ++this.requeteModal;
    const modalAuDepart = this.requeteModal;
    const installation = this.installationId;
    const graphe = this.graphe;
    const estValide = () => installation === this.installationId && graphe === this.graphe &&
      requete === (initialiser ? this.requeteInitiale : this.requeteModal);
    const codes = this.graphe.series.map(s => s.point_mesure.nom);
    if (!codes.length) {
      this.chargement = false;
      this.chargementModal = false;
      this.cdr.markForCheck();
      return;
    }

    try {
      const raw = await this.installationService.getInstallationData(this.installationId, codes, mode, dateFin);
      if (!estValide()) return;
      const donnees = this.parseDonnees(raw, codes);
      if (!initialiser || modalAuDepart === this.requeteModal) this.donneesActuelles = donnees;

      if (initialiser) {
        if (!isPlatformBrowser(this.platformId)) {
          this.chargement = false;
          this.cdr.markForCheck();
          return;
        }
        if (this.chartHost) {
          this.initialiserGraphiqueCarteInternal(donnees);
        } else {
          this.donneesEnAttente = donnees;
          this.chargement = false;
        }
      } else {
        this.chargementModal = false;
        this.refreshToken++;
      }
    } catch (err: unknown) {
      if (!estValide()) return;
      if (initialiser) {
        this.erreur = (err as Error)?.message ?? 'Erreur de chargement';
        this.chargement = false;
      } else {
        this.chargementModal = false;
      }
    }
    this.cdr.markForCheck();
  }

  private initialiserGraphiqueCarteInternal(data: GrapheDataPoint[]): void {
    if (!this.chartHost) return;
    this.chartRoot?.dispose();
    this.chartRoot = createGrapheChart(this.chartHost, this.graphe, data);
    this.chargement = false;
    this.cdr.markForCheck();
  }

  /** "minute" → jour, "jour" → mois, "mois" → annee */
  private intervalVersMode(interval: string): ModeAffichageGraphique {
    switch (interval) {
      case 'mois': return 'annee';
      case 'jour': return 'mois';
      default:     return 'jour';
    }
  }

  private parseDonnees(raw: unknown, codes: string[]): GrapheDataPoint[] {
    const brut = this.extraireTableauBrut(raw);
    const pointsParDate = new Map<number, GrapheDataPoint>();

    for (const ligne of brut) {
      if (ligne == null || typeof ligne !== 'object') continue;
      const ligneObj = ligne as Record<string, unknown>;
      const date = this.extraireTimestamp(ligneObj);
      if (date == null) continue;
      const point: GrapheDataPoint = pointsParDate.get(date) ?? { date };
      for (const code of codes) {
        const val = Number(ligneObj[code]);
        if (!isNaN(val)) point[code] = val;
      }
      pointsParDate.set(date, point);
    }

    return [...pointsParDate.values()].sort((a, b) => a.date - b.date);
  }

  private extraireTableauBrut(data: unknown): unknown[] {
    if (Array.isArray(data)) return data;
    if (!data || typeof data !== 'object') return [];
    const val = (data as Record<string, unknown>)['datas'];
    return Array.isArray(val) ? val : [];
  }

  private extraireTimestamp(ligne: Record<string, unknown>): number | null {
    for (const candidat of [ligne['date'], ligne['datetime'], ligne['horodate'], ligne['timestamp'], ligne['ts']]) {
      if (typeof candidat === 'number' && Number.isFinite(candidat)) {
        return candidat > 1_000_000_000_000 ? candidat : candidat * 1000;
      }
      if (typeof candidat === 'string' && candidat.trim().length > 0) {
        const ts = Date.parse(candidat);
        if (!isNaN(ts)) return ts;
      }
    }
    return null;
  }
}
